// ═══════════════════════════════════════════════════════════════════════════
//  A20 · C1 — the anonymous surface's capture buffer: console + network, no DOM.
//
//  ── WHY NOT JUST USE useRecorder ────────────────────────────────────────────
//  Because on the anonymous path a DOM recording is bytes we are guaranteed to
//  throw away. POST /api/v1/reports/anonymous strips `captureGzipB64` and forces
//  `capture.captured = false` server-side, on the stated grounds that "an
//  unauthenticated origin must not be able to push megabytes into our storage"
//  (bug-report.routes.ts:199-213). Running rrweb on a login page to produce a
//  multi-megabyte gzip that the server deletes on arrival would cost every
//  visitor the rrweb chunk and cost the user their upload, and buy nobody
//  anything.
//
//  But `capture.console` and `capture.network` are NOT stripped — the route
//  spreads the submitted capture and overrides only `captured` — so they arrive
//  and land on the ticket. That is exactly the acceptance criterion for C1: a
//  report filed from /login while logged out arrives with route + console and no
//  identity. Setting `captureDisabled: true` on the shared provider (which is
//  what apps/trust does) would have satisfied "no replay" and silently failed
//  "with console", because useRecorder's `disabled` branch installs NO shims at
//  all — no rrweb, no console plugin, no network patch.
//
//  So: the provider gets `captureDisabled: true` (no rrweb, and the dialog
//  truthfully says session capture is unavailable), and this hook supplies the
//  two buffers that CAN travel. The transport merges them in.
//
//  ── PROVENANCE ──────────────────────────────────────────────────────────────
//  PORTED COPY of TPRM7 packages/ui/src/reporter-anon/useConsoleBuffer.ts, which
//  is itself the shim pair lifted out of this repo's customer-portal reduced
//  recorder (customer-portal/src/components/reporter/useRecorder.ts) — that file
//  already solved exactly this problem for a build with no rrweb dependency.
//  Three copies, three trees, no shared package between the two repos. Keep them
//  diffable.
//
//  NOTE for this app specifically: ../useRecorder here IS the full rrweb
//  recorder (unlike customer-portal's). It is deliberately not used on the
//  anonymous path — see above.
//
//  ── THE GOVERNING RULE IS UNCHANGED ─────────────────────────────────────────
//  This must never break the host app, and it is patching globals on the ONE
//  page where a break means nobody can sign in. Both shims install inside
//  try/catch, always call the original first, restore on unmount, and degrade
//  to an empty buffer rather than throwing.
// ═══════════════════════════════════════════════════════════════════════════

import * as React from 'react';
import type { ConsoleEntry, NetworkEntry } from '../types';

/** Hard ceilings so a chatty page cannot grow the buffer without bound. */
const MAX_CONSOLE = 200;
const MAX_NETWORK = 200;
/** Per-argument truncation. The server caps at 4000; stay well inside it. */
const MAX_ARG_CHARS = 2000;
const MAX_ARGS = 10;

type StopFn = () => void;

export interface AnonymousBuffer {
  /** True once the shims are installed and collecting. */
  active: boolean;
  /** Snapshot for submission. Safe to call when inactive (returns empties). */
  collect: () => { console: ConsoleEntry[]; network: NetworkEntry[]; durationMs: number };
}

/**
 * Strip query + hash.
 *
 * NOT cosmetic on this surface: /reset-password is reached by a GoTrue email
 * link that carries the recovery session IN THE URL HASH (implicit flow — see
 * ResetPassword.tsx's header), and this hook is mounted on that route. A
 * recovery link that travelled inside a bug report would be a live credential
 * sitting in a support ticket.
 */
function safeUrl(input: string): string {
  try {
    const u = new URL(input, typeof location !== 'undefined' ? location.href : 'http://localhost');
    return u.origin + u.pathname;
  } catch {
    return String(input).split('?')[0]!.split('#')[0]!;
  }
}

/** Stringify one console argument without ever throwing on a cyclic value. */
function stringifyArg(value: unknown): string {
  try {
    if (typeof value === 'string') return value.slice(0, MAX_ARG_CHARS);
    if (value instanceof Error) return `${value.name}: ${value.message}`.slice(0, MAX_ARG_CHARS);
    return JSON.stringify(value)?.slice(0, MAX_ARG_CHARS) ?? String(value).slice(0, MAX_ARG_CHARS);
  } catch {
    return '[unserialisable]';
  }
}

const CONSOLE_LEVELS = ['log', 'info', 'warn', 'error'] as const;
type ConsoleLevel = (typeof CONSOLE_LEVELS)[number];

export function useConsoleBuffer(opts: { disabled?: boolean }): AnonymousBuffer {
  const { disabled = false } = opts;

  const [active, setActive] = React.useState(false);
  const consoleBuf = React.useRef<ConsoleEntry[]>([]);
  const networkBuf = React.useRef<NetworkEntry[]>([]);
  const startedAt = React.useRef<number>(Date.now());

  React.useEffect(() => {
    if (disabled || typeof window === 'undefined') return;

    startedAt.current = Date.now();
    consoleBuf.current = [];
    networkBuf.current = [];

    const undo: StopFn[] = [];

    // ── console ───────────────────────────────────────────────────────────
    // The original is always called FIRST, so devtools behaviour is unchanged
    // even if the buffer push throws.
    try {
      for (const level of CONSOLE_LEVELS) {
        const orig = console[level as ConsoleLevel] as ((...args: unknown[]) => void) | undefined;
        if (typeof orig !== 'function') continue;
        console[level as ConsoleLevel] = function patched(...args: unknown[]) {
          orig.apply(console, args);
          try {
            consoleBuf.current.push({
              level,
              payload: args.slice(0, MAX_ARGS).map(stringifyArg),
              at: Date.now() - startedAt.current,
            });
            if (consoleBuf.current.length > MAX_CONSOLE) consoleBuf.current.shift();
          } catch {
            /* the buffer is a nicety; never let it break a log call */
          }
        } as typeof console.log;
        undo.push(() => {
          console[level as ConsoleLevel] = orig as typeof console.log;
        });
      }
    } catch {
      /* fall through — a partial patch is still restored by `undo` */
    }

    // ── network ───────────────────────────────────────────────────────────
    // Bodies are never captured. On an auth page the useful facts are which
    // GoTrue call was made and what it answered: POST /auth/v1/token → 400 is
    // the entire diagnosis of most "I cannot sign in" reports.
    const push = (entry: NetworkEntry) => {
      networkBuf.current.push(entry);
      if (networkBuf.current.length > MAX_NETWORK) networkBuf.current.shift();
    };

    try {
      const origFetch = window.fetch;
      if (typeof origFetch === 'function') {
        window.fetch = function patchedFetch(this: unknown, ...args: Parameters<typeof fetch>) {
          const t0 = Date.now();
          const req = args[0];
          const url =
            typeof req === 'string'
              ? req
              : req instanceof URL
                ? req.href
                : ((req as Request)?.url ?? '');
          const method = (
            args[1]?.method ??
            (req instanceof Request ? req.method : undefined) ??
            'GET'
          ).toUpperCase();
          return origFetch.apply(this as never, args).then(
            (res) => {
              push({
                method,
                url: safeUrl(url),
                status: res.status,
                durationMs: Date.now() - t0,
                at: t0 - startedAt.current,
              });
              return res;
            },
            (err) => {
              push({
                method,
                url: safeUrl(url),
                failed: true,
                durationMs: Date.now() - t0,
                at: t0 - startedAt.current,
              });
              throw err;
            },
          );
        } as typeof fetch;
        undo.push(() => {
          window.fetch = origFetch;
        });
      }

      const origOpen = window.XMLHttpRequest?.prototype?.open;
      const origSend = window.XMLHttpRequest?.prototype?.send;
      if (origOpen && origSend) {
        const KEY = '__psAnonReporter' as const;
        type Tagged = XMLHttpRequest & { [KEY]?: { method: string; url: string; t0: number } };

        window.XMLHttpRequest.prototype.open = function patchedOpen(
          this: Tagged,
          method: string,
          url: string | URL,
          ...rest: unknown[]
        ) {
          this[KEY] = { method: String(method).toUpperCase(), url: String(url), t0: 0 };
          return origOpen.apply(this, [method, url, ...rest] as never);
        } as typeof origOpen;

        window.XMLHttpRequest.prototype.send = function patchedSend(
          this: Tagged,
          ...rest: unknown[]
        ) {
          const meta = this[KEY];
          if (meta) {
            meta.t0 = Date.now();
            this.addEventListener('loadend', () => {
              push({
                method: meta.method,
                url: safeUrl(meta.url),
                status: this.status || undefined,
                failed: this.status === 0,
                durationMs: Date.now() - meta.t0,
                at: meta.t0 - startedAt.current,
              });
            });
          }
          return origSend.apply(this, rest as never);
        } as typeof origSend;

        undo.push(() => {
          window.XMLHttpRequest.prototype.open = origOpen;
          window.XMLHttpRequest.prototype.send = origSend;
        });
      }

      setActive(true);
    } catch {
      // Degrade, never throw. The user can still file a text-only report.
      setActive(false);
    }

    return () => {
      setActive(false);
      for (const fn of undo.reverse()) {
        try {
          fn();
        } catch {
          /* ignore */
        }
      }
    };
  }, [disabled]);

  const collect = React.useCallback(
    () => ({
      console: [...consoleBuf.current],
      network: [...networkBuf.current],
      durationMs: Date.now() - startedAt.current,
    }),
    [],
  );

  return { active, collect };
}
